import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { GroupedSelect } from '@components/ui/GroupedSelect/GroupedSelect';
import type { IGroupedOptions, ISelectionOption } from '@components/Pages/AnalyticsPage/analytics.types';
import type { IScheduleProfession, SiteId } from '@interfaces/IScheduler';
import type { ISiteProfession } from '@apis/professions';
import { useSiteProfessions } from '@hooks/useSiteProfessions';
import { groupProfessionsByTradeGroup } from '@d-orca/shared/client/utils';
import { tradeLabel } from '@utils/tradeLabel';
import { ProfessionPill } from '@components/ui/ProfessionPill/ProfessionPill';
import styles from './ProfessionPicker.module.css';

interface IProfessionPickerProps {
  id?: string;
  siteId: SiteId;
  /** Current draft profession snapshot, or undefined when unassigned. */
  value: IScheduleProfession | undefined;
  onChange: (profession: IScheduleProfession | undefined) => void;
  /** Lock the trigger — the profession is fixed once the task is committed.
   *  Says so under the field, rather than blaming the catalog. */
  disabled?: boolean;
}

/** Denormalize a catalog profession into the stored profession snapshot. */
function toProfession(p: ISiteProfession): IScheduleProfession {
  return {
    professionId: p.id,
    tradeId: p.tradeId,
    tradeGroup: p.tradeGroup,
    specialty: p.specialty,
    contractor: p.contractor,
    backgroundColor: p.backgroundColor,
  };
}

/** Canonical option label — mirrors what `ProfessionPill` renders (Tags-tab pattern). */
function professionLabel(t: TFunction, a: IScheduleProfession): string {
  let label = tradeLabel(t, a.tradeId) || a.tradeGroup || '';
  if (a.specialty) label += `, ${a.specialty}`;
  return label;
}

/**
 * Single clearable profession picker for a task — replaces the retired
 * trade `<select>` + assignee free-text input. Options come from the live site
 * catalog (`useSiteProfessions`); each option and the selected value render the
 * shared `ProfessionPill`. Delegates all dropdown mechanics to the shared
 * `GroupedSelect` (grouping via `groupProfessionsByTradeGroup`, sorted like the
 * Issues-page profession selector), passing `ProfessionPill` for the item/value
 * slots so the mandated pill visual is preserved. Profession is optional, so the
 * clearable slot resets it. The trigger is inert when the caller locks it or the
 * catalog is empty (loading, unconfigured, or upstream error); a hint under it
 * names which.
 */
export function ProfessionPicker({ id, siteId, value, onChange, disabled: forced = false }: IProfessionPickerProps) {
  const { t } = useTranslation();
  const professions = useSiteProfessions(siteId);
  const disabled = forced || professions.length === 0;
  const hint = forced ? 'locked' : 'empty';

  // Build grouped `GroupedSelect` options + a lookup back to the full snapshot,
  // keyed by professionId (the option id GroupedSelect echoes on change).
  const { groups, professionById } = useMemo(() => {
    const byId = new Map<string, IScheduleProfession>();
    const grouped = groupProfessionsByTradeGroup(professions, (p) => p, t);
    const groupOptions: IGroupedOptions[] = grouped.map(({ group, groupLabel, items }) => ({
      groupId: group,
      groupLabel,
      options: items.map((p): ISelectionOption => {
        const profession = toProfession(p);
        byId.set(profession.professionId, profession);
        return { id: profession.professionId, label: professionLabel(t, profession) };
      }),
    }));
    return { groups: groupOptions, professionById: byId };
  }, [professions, t]);

  const renderPill = (opt: ISelectionOption): ReactNode => {
    const profession = professionById.get(opt.id);
    return profession ? <ProfessionPill profession={profession} showContractor /> : opt.label;
  };

  return (
    <>
      <GroupedSelect
        {...(id ? { id } : {})}
        value={value?.professionId ?? null}
        onChange={(next) => onChange(next === null ? undefined : professionById.get(next))}
        groups={groups}
        placeholder={t('scheduler.panel.profession.placeholder')}
        clearLabel={t('scheduler.panel.profession.clear')}
        ariaLabel={t('scheduler.panel.fields.profession')}
        disabled={disabled}
        clearable
        renderItem={renderPill}
        renderValue={renderPill}
      />
      {disabled && <span className={styles.hint}>{t(`scheduler.panel.profession.${hint}`)}</span>}
    </>
  );
}
